import client from './client';
import { User } from '../stores/authStore';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface CustomerSignupData {
  email: string;
  password: string;
  phone: string;
  first_name: string;
  last_name?: string;
}

export interface StoreSignupData {
  email: string;
  password: string;
  phone: string;
  business_name: string;
  vertical?: 'grocery' | 'food';
  business_address?: string;
  business_phone?: string;
  upi_id?: string;
  default_discount_percent?: number;
  latitude?: number;
  longitude?: number;
}

export interface DeliverySignupData {
  email: string;
  password: string;
  phone: string;
  first_name: string;
  last_name?: string;
  // Aadhaar KYC
  aadhaar_number: string;
  aadhaar_name: string;
  date_of_birth: string;
  gender: string;
  // Address
  address: string;
  city: string;
  state: string;
  pincode: string;
  // Vehicle
  vehicle_type: string;
  vehicle_number: string;
  driving_license_number?: string;
}

export interface AuthResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user: User;
  is_new_user?: boolean;
}

export interface OTPRequestData {
  identifier: string;
  identifier_type: 'phone' | 'email';
}

export interface OTPVerifyData {
  identifier: string;
  identifier_type: 'phone' | 'email';
  otp_code: string;
}

export interface OTPResponse {
  message: string;
  expires_in: number;
  dev_otp?: string;
}

export const authAPI = {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await client.post<AuthResponse>('/api/auth/login', credentials);
    return response.data;
  },

  async requestOTP(data: OTPRequestData): Promise<OTPResponse> {
    const response = await client.post<OTPResponse>('/api/auth/otp/request', data);
    return response.data;
  },

  async verifyOTP(data: OTPVerifyData): Promise<AuthResponse> {
    const response = await client.post<AuthResponse>('/api/auth/otp/verify', data);
    return response.data;
  },

  // ---- Dual OTP signup (verify both phone + email) ----
  async requestDualOTP(data: { phone: string; email: string }) {
    const response = await client.post<{
      message: string; expires_in: number;
      dev_phone_otp?: string | null; dev_email_otp?: string | null;
    }>('/api/auth/otp/request-dual', data);
    return response.data;
  },

  async verifyDualOTP(data: {
    phone: string; email: string;
    phone_otp: string; email_otp: string;
    first_name?: string; last_name?: string;
  }): Promise<AuthResponse> {
    const response = await client.post<AuthResponse>('/api/auth/otp/verify-dual', data);
    return response.data;
  },

  async registerCustomer(data: CustomerSignupData): Promise<AuthResponse> {
    const response = await client.post<AuthResponse>('/api/auth/register/customer', data);
    return response.data;
  },

  async registerStore(data: StoreSignupData): Promise<AuthResponse> {
    const response = await client.post<AuthResponse>('/api/auth/register/store', data);
    return response.data;
  },

  async registerDelivery(data: DeliverySignupData): Promise<AuthResponse> {
    const response = await client.post<AuthResponse>('/api/auth/register/delivery', data);
    return response.data;
  },

  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    const response = await client.post<AuthResponse>('/api/auth/refresh', {
      refresh_token: refreshToken,
    });
    return response.data;
  },

  async getCurrentUser(): Promise<User> {
    const response = await client.get<User>('/api/auth/me');
    return response.data;
  },

  async getCustomerProfile(): Promise<CustomerProfile> {
    const response = await client.get<CustomerProfile>('/api/auth/me/customer');
    return response.data;
  },

  async updateCustomerProfile(data: CustomerProfileUpdate): Promise<CustomerProfile> {
    const response = await client.put<CustomerProfile>('/api/auth/me/customer', data);
    return response.data;
  },
};

export interface CustomerProfile {
  id: number;
  user_id: number;
  first_name: string;
  last_name?: string | null;
  default_address?: string | null;
  profile_photo_url?: string | null;
  total_orders: number;
  total_spent: number;
  rating: number;
}

export interface CustomerProfileUpdate {
  first_name?: string;
  last_name?: string;
  default_address?: string;
  profile_photo_url?: string;
}

